import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { productService, chatService, API_ROOT_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await productService.getProduct(id);
        setProduct(res.data);
      } catch (err) {
        setError('No se pudo cargar el producto.');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleContact = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setChatLoading(true);
    try {
      // Crear o abrir conversación con el proveedor
      const res = await chatService.createConversation(product.supplier);
      const conversationId = res.data.id;
      navigate(`/chat?conversation=${conversationId}`);
    } catch (err) {
      setError('No se pudo iniciar el chat con el proveedor.');
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!product) return null;

  const getSpec = (key, fallback) => {
    // Busca en campos dedicados primero, luego en el JSON de especificaciones
    return product[key] || product?.especificaciones?.[key] || fallback || '-';
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            {product.images && product.images.length > 0 ? (
              <img
                src={product.images[0].image.startsWith('http') ? 
                  product.images[0].image.replace(/^http:/, 'https:') : 
                  `${API_ROOT_URL}${product.images[0].image}`}
                alt={product.title}
                style={{ width: '100%', borderRadius: 8 }}
              />
            ) : (
              <Box sx={{ width: '100%', height: 250, bgcolor: '#f3f4f6', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Sin imagen</Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography variant="h4" gutterBottom>{product.title}</Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {product.category ? product.category : 'Sin categoría'}
            </Typography>
            
            {/* Información del proveedor */}
            {product.supplier && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  <strong>Proveedor:</strong>
                </Typography>
                <Typography variant="body2">
                  {product.supplier.business_profile?.company_name || product.supplier.username}
                </Typography>
                {product.supplier.business_profile?.company_description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {product.supplier.business_profile.company_description}
                  </Typography>
                )}
              </Box>
            )}
            
            <Typography variant="body1" sx={{ mb: 2 }}>{product.description}</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><b>Dimensiones:</b> {getSpec('dimensions')}</Typography>
              <Typography variant="body2"><b>Espesor:</b> {getSpec('thickness')}</Typography>
              <Typography variant="body2"><b>Peso:</b> {getSpec('weight')}</Typography>
              <Typography variant="body2"><b>Norma:</b> {getSpec('standard')}</Typography>
              <Typography variant="body2"><b>MOQ:</b> {getSpec('moq')}</Typography>
            </Box>
            {product.contact_for_price ? (
              <Typography variant="h6" color="warning.main" sx={{ mb: 2 }}>
                Contactar para cotización
              </Typography>
            ) : (
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                Precio: ${product.price}
              </Typography>
            )}
            {/* Botón de contactar proveedor solo para compradores autenticados */}
            {user && user.role === 'consumer' && (
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleContact}
                disabled={chatLoading}
              >
                {chatLoading ? <CircularProgress size={24} color="inherit" /> : 'Contactar proveedor'}
              </Button>
            )}
            {!user && (
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => navigate('/login')}
              >
                Inicia sesión para contactar
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ProductDetail; 